import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { isEmpty } from 'ramda'
import { showUpdateGenerateTextModal } from '@/features/SmartDescription/generate-text'
import { createModal, useModalize } from '@/components/modalize/mui'
import { showChangeToneModal, SmartButton, ModalTitle } from '@/features/SmartDescription'
import { colors } from '@/styles'
import RightArrowSVG from './right-arrow.svg?react'

const SmartOptionModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledOptionSmartButton = styled(SmartButton)`
  color: ${colors.grey24} !important;
  font-size: 16px;
  padding: 16px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.grey88} !important;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const ButtonTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`

const StyledArrowSVG = styled(RightArrowSVG)`
  margin-left: 8px;
`

const Description = styled.p`
  font-family: 'Inter';
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;

  color: ${colors.grey32};
  margin: 0;
  text-align: left;
`

const SmartOptionsModal = ({
  generateValues,
  listingId,
  values,
  setDescriptionFormValue,
  handleEmptyDescription
}) => {
  const { close } = useModalize()
  const { t } = useTranslation()

  const handleGenerateTextClick = () => {
    close()
    showChangeToneModal({
      listingId: listingId,
      formValues: generateValues,
      initAction: 'create',
      heading: t('l.smart_description.generate_text.generate_text.title', 'Generate text'),
      setDescriptionFormValue: setDescriptionFormValue
    })
  }

  const handleCleanUpClick = () => {
    if (isEmpty(values?.description)) {
      handleEmptyDescription()
    } else {
      close()
      showUpdateGenerateTextModal({
        description: values?.description,
        listingId: listingId,
        tone: 'cleanup',
        heading: t('l.smart_description.generate_text.generate_text.title', 'Generate text'),
        setDescriptionFormValue: setDescriptionFormValue,
        eventId: 'application-description-ai-cleanup-click'
      })
    }
  }

  const handleChangeToneClick = () => {
    if (isEmpty(values?.description)) {
      handleEmptyDescription()
    } else {
      close()
      showChangeToneModal({
        description: values?.description,
        listingId: listingId,
        heading: t('l.smart_description.change_tone.change_tone.title', 'Change tone'),
        setDescriptionFormValue: setDescriptionFormValue
      })
    }
  }

  return (
    <div>
      <ModalTitle heading="Smart text" onClose={close} showIcon={false} />
      <SmartOptionModalWrapper>
        <StyledOptionSmartButton
          theme="text"
          id="application-description-ai-gentext"
          onClick={handleGenerateTextClick}
        >
          <ButtonWrapper>
            <ButtonTitleWrapper>
              {t('l.smart_description.generate_text.generate_text.title', 'Generate text')}
              <StyledArrowSVG />
            </ButtonTitleWrapper>
            <Description>
              {t(
                'l.smart_description.generate_text.tooltip.text',
                `Generate engaging listing descriptions instantly, powered by ChatGPT AI.`
              )}
            </Description>
          </ButtonWrapper>
        </StyledOptionSmartButton>
        <StyledOptionSmartButton
          theme="text"
          id="application-description-ai-cleanup"
          onClick={handleCleanUpClick}
        >
          <ButtonWrapper>
            <ButtonTitleWrapper>
              {t('l.smart_description.generate_text.clean_up.title', 'Clean up')}
              <StyledArrowSVG />
            </ButtonTitleWrapper>
            <Description>
              {t(
                'l.smart_description.clean_up.tooltip.text',
                `Enter a description and we will correct your grammar and clean the layout, powered by ChatGPT AI.`
              )}
            </Description>
          </ButtonWrapper>
        </StyledOptionSmartButton>
        <StyledOptionSmartButton
          theme="text"
          id="application-description-ai-tone"
          onClick={handleChangeToneClick}
        >
          <ButtonWrapper>
            <ButtonTitleWrapper>
              {t('l.smart_description.change_tone.change_tone.title', 'Change tone')}
              <StyledArrowSVG />
            </ButtonTitleWrapper>
            <Description>
              {t(
                'l.smart_description.change_tone.tooltip.text',
                `Enter a description and we will help you change the tone, powered by ChatGPT AI.`
              )}
            </Description>
          </ButtonWrapper>
        </StyledOptionSmartButton>
      </SmartOptionModalWrapper>
    </div>
  )
}

export const showSmartOptionsModal = createModal(SmartOptionsModal, {
  hasHeader: false,
  sheet: false
})
